import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Pill, Clock, Beaker, Link2, CalendarClock, StickyNote, ChevronRight, ChevronLeft, Copy, Check, ExternalLink } from 'lucide-react';

const Wrap = ({ children }) => (
    <div className="px-4 sm:px-6 lg:px-8 py-6 max-w-[1100px] mx-auto animate-fade-in" data-testid="supplements-page">{children}</div>
);

const formatDate = (iso) => {
    if (!iso) return '';
    const [y, m, d] = iso.split('-').map(Number);
    return new Date(y, m - 1, d).toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' });
};

// EL ENLACE DICE A DÓNDE VA (Gonzalo, 4-09, punto 84). «Comprar con descuento 1» y «Comprar
// con descuento 2» no le dicen al cliente nada de lo que va a abrir. El catálogo solo guarda
// la URL, así que el nombre de la tienda sale del dominio: sin «www.» ni terminación y con
// la primera en mayúscula («Comprar en Bulevip»). Las tiendas cuya marca se escribe de otra
// forma van en esta lista, que es corta a propósito: son las que hay en el catálogo.
const MARCA_DE_LA_TIENDA = {
    fullgas: 'FullGas', hsn: 'HSN', amzn: 'Amazon', amazon: 'Amazon', iherb: 'iHerb',
    biotechusa: 'BioTechUSA', masmusculo: 'MASmusculo', nootropicsdepot: 'Nootropics Depot',
    primemale: 'Prime Male', tantrumcbd: 'Tantrum CBD', openfoodfacts: 'Open Food Facts',
    myprotein: 'Myprotein', prozis: 'Prozis', decathlon: 'Decathlon', ostrovit: 'OstroVit',
    bulevip: 'Bulevip', bodyshock: 'Bodyshock', traumaceutics: 'Traumaceutics',
    carmencita: 'Carmencita', vademecum: 'Vademecum',
};
// Y NO TODO LO ENLAZADO SE COMPRA: el Ursobilane apunta a su prospecto en Vademecum y la sal
// yodada a su ficha en Open Food Facts. A eso no se le dice «Comprar»: se abre para leerlo.
const NO_SON_TIENDAS = ['Vademecum', 'Open Food Facts'];
export const nombreDeLaTienda = (url) => {
    let host = '';
    try { host = new URL(String(url || '')).hostname.toLowerCase(); } catch { return ''; }
    // Fuera los prefijos que no son la marca («www.», «shop.», «es.») y la terminación.
    const trozos = host.split('.').filter(Boolean);
    while (trozos.length > 2 && ['www', 'shop', 'es', 'en', 'm', 'tienda', 'store'].includes(trozos[0])) trozos.shift();
    const base = trozos.length > 1 ? trozos[trozos.length - 2] : trozos[0] || '';
    if (!base) return '';
    return MARCA_DE_LA_TIENDA[base] || base.charAt(0).toUpperCase() + base.slice(1);
};
// LA DIRECCIÓN DE UN ENLACE, venga como venga: los protocolos viejos guardaron la URL
// suelta y los bloques del coach guardan `{label, url}`. Aquí ya se resolvía a mano en
// cuatro sitios; ahora hay uno.
const urlDeEnlace = (enlace) => (typeof enlace === 'string' ? enlace : enlace?.url) || '';

// «Comprar en FullGas»; y si el enlace trae su etiqueta (los bloques del coach la llevan:
// «Hydropeptides», «MAP»), «Hydropeptides · Comprar en FullGas», que es lo que distingue
// dos enlaces a la misma tienda.
const textoDelEnlace = (enlace) => {
    const url = urlDeEnlace(enlace);
    const tienda = nombreDeLaTienda(url);
    if (!tienda) return 'Comprar con descuento';
    // «COMPRAR CON DESCUENTO», SU LITERAL, Y LA TIENDA (Francisco, 17-09).
    //
    // Su artefacto cambia «Dónde encontrarlo» por «Comprar con descuento»: «Dónde
    // encontrarlo es vago, parece que te va a decir en qué tienda buscarlo y luego te lleva
    // a una web; Comprar con descuento pone por delante lo que gana él, y engancha con el
    // código de abajo, que es el mismo 20 %».
    //
    // «Dónde encontrarlo» ya no estaba, pero su literal tampoco: desde el punto 84 de
    // Gonzalo (4-09) el enlace decía «Comprar en FullGas», porque «Comprar con descuento 1»
    // y «2» no dicen a dónde llevan. Las dos cosas son ciertas y caben juntas, así que el
    // enlace lleva su frase entera y la tienda detrás. Lo que no se toca es lo que no se
    // compra: el prospecto del Ursobilane sigue siendo «Ver la ficha en Vademecum», que
    // llamar «comprar» a un prospecto sería peor que vago.
    const verbo = NO_SON_TIENDAS.includes(tienda) ? 'Ver la ficha en' : 'Comprar con descuento en';
    return enlace?.label ? `${enlace.label} · ${verbo} ${tienda}` : `${verbo} ${tienda}`;
};
// Y CUANDO EL COMBO LLEVA EL NOMBRE DE LOS DOS BOTES, SE USA (Gonzalo, 4-09, punto 84;
// cerrado el 13-09). El punto pedía «cada enlace con el nombre de su tienda» y eso ya estaba,
// pero los combos de los protocolos migrados seguían saliendo «Comprar en FullGas 1» y
// «Comprar en FullGas 2»: dos enlaces a la misma tienda, sin etiqueta, y un número que no
// dice cuál es cuál. El nombre de los dos botes SÍ está, en el título de la ficha: «Whey
// Isolate + crema de arroz», «Hydropeptides o MAP». Si el título se parte en tantos trozos
// como enlaces hay, cada uno se queda con el suyo y sale «Whey Isolate · Comprar en FullGas»,
// que es el mismo formato que ya usan los bloques del coach con su `label`.
//
// El número se queda como último respaldo: un combo cuyo título no se parta -- o que se parta
// en otra cantidad -- no puede quedarse con dos enlaces indistinguibles.
const TROZOS_DEL_TITULO = /\s+(?:\+|o|y)\s+/i;
const nombresDelCombo = (titulo, cuantos) => {
    const trozos = String(titulo || '').split(TROZOS_DEL_TITULO).map((t) => t.trim()).filter(Boolean);
    return trozos.length > 1 && trozos.length === cuantos ? trozos : null;
};
// Los enlaces de un suplemento, ya con su texto. Si dos caen en la misma tienda y no traen
// etiqueta (los protocolos viejos guardaron solo la URL) se distinguen por el trozo de título
// que les toca y, si no lo hay, se numeran. Con uno solo, nunca hay número.
export const textosDeLosEnlaces = (enlaces, titulo) => {
    const lista = enlaces || [];
    const nombres = nombresDelCombo(titulo, lista.length);
    const textos = lista.map((e, i) => (
        (nombres && !e?.label) ? textoDelEnlace({ ...(typeof e === 'string' ? { url: e } : e), label: nombres[i] })
            : textoDelEnlace(e)
    ));
    const cuantos = textos.reduce((acc, t) => ({ ...acc, [t]: (acc[t] || 0) + 1 }), {});
    const vistos = {};
    return textos.map((t) => {
        if (cuantos[t] < 2) return t;
        vistos[t] = (vistos[t] || 0) + 1;
        return `${t} ${vistos[t]}`;
    });
};

/**
 * LAS FOTOS DE UNA FICHA, SIEMPRE COMO LISTA (Jesús, 6-09).
 *
 * Del combo de post-entreno pidió «la foto de la proteína y la crema de arroz debajo»: dos
 * botes, uno encima del otro. Hasta hoy cada ficha tenía UNA foto (`imagen`), así que se
 * añade `imagenes` sin tocar la de siempre: si la ficha no la trae, la lista es la de antes
 * con un solo elemento y la tarjeta se ve exactamente igual que ahora.
 *
 * Cada entrada puede ser la URL suelta o `{url, enlace, alt}`. Y una entrada SIN url es
 * legítima: es el bote del que todavía no tenemos foto -- sale con el icono de pastilla,
 * como cualquier ficha sin foto -- y sigue llevando a su página.
 */
const fotosDe = (item) => (item.imagenes?.length ? item.imagenes : [item.imagen])
    .map((f) => (f && typeof f === 'object'
        ? { url: f.url || '', enlace: f.enlace, alt: f.alt }
        : { url: f || '' }));

/**
 * LA COLUMNA DE FOTOS, Y EL BOTE SE PINCHA (Jesús, 6-09: «que se pueda hacer clic en el
 * bote y que el bote lleve a la página»).
 *
 * A dónde va cada bote: al enlace de la propia foto si lo trae y, si no, al primer enlace
 * de la ficha, que es el único destino que se puede dar por bueno cuando hay una sola.
 * Sin ninguna dirección la foto NO se envuelve en un enlace: un <a> sin destino se puede
 * pinchar y no lleva a ninguna parte, que es peor que no poder pincharlo.
 *
 * EL CONTROL DE ERROR ES POR FOTO. Antes había un `imgError` para la única que había; con
 * varias, una que no cargue no puede tumbar a las demás al icono de pastilla.
 */
const ColumnaDeFotos = ({ fotos, alt, enlacePorDefecto }) => {
    const [fallos, setFallos] = useState({});
    return (
        <div className="flex flex-col gap-2 flex-shrink-0" data-testid="fotos-suplemento">
            {fotos.map((foto, i) => {
                const caja = (
                    <div className="w-28 h-28 sm:w-36 sm:h-36 rounded-xl bg-muted/40 flex items-center justify-center flex-shrink-0 overflow-hidden p-2">
                        {foto.url && !fallos[i]
                            ? <img src={foto.url} alt={foto.alt || alt} className="max-h-full max-w-full object-contain"
                                onError={() => setFallos((previos) => ({ ...previos, [i]: true }))} />
                            : <Pill className="w-12 h-12 text-brand/50" />}
                    </div>
                );
                const destino = urlDeEnlace(foto.enlace) || enlacePorDefecto || '';
                return destino
                    ? <a key={i} href={destino} target="_blank" rel="noopener noreferrer"
                        title={foto.alt || alt} data-testid="foto-suplemento-enlace">{caja}</a>
                    : <React.Fragment key={i}>{caja}</React.Fragment>;
            })}
        </div>
    );
};

/**
 * LA OTRA OPCIÓN, EN SU PROPIA LÍNEA (Jesús, 6-09): «quiero que ponga "Proteína
 * alternativa:" y salga esta [la de clara de huevo]».
 *
 * No es un enlace más de la fila de comprar: es una alternativa al bote de arriba, y lo que
 * la explica es su etiqueta. Por eso va aparte, con la etiqueta en negrita y dos puntos.
 * El texto del enlace se compone SIN la etiqueta («Comprar en FullGas» y no «Proteína
 * alternativa · Comprar en FullGas»), que ya la lleva delante.
 */
const EnlacesAlternativos = ({ enlaces }) => (
    <div className="mt-1.5 space-y-1" data-testid="enlaces-alternativos">
        {enlaces.map((e, i) => (
            <div key={i} className="flex flex-wrap items-center gap-1 text-xs">
                <span className="font-semibold text-foreground">{e.label}:</span>
                <a href={urlDeEnlace(e)} target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-brand hover:underline font-semibold">
                    <Link2 className="w-3 h-3" /> {textoDelEnlace({ url: urlDeEnlace(e) })}
                </a>
            </div>
        ))}
    </div>
);

const SupplementCard = ({ item }) => {
    const [imgError, setImgError] = useState(false);
    return (
    <div className="surface p-0 overflow-hidden flex flex-col sm:flex-row" data-testid="supplement-card">
        {/* En móvil la foto iba oculta y la tarjeta se quedaba en puro texto. Ahora
            sale también ahí, como una banda arriba, y en escritorio como columna. */}
        <div className="flex h-28 sm:h-auto sm:w-44 items-center justify-center bg-muted/40 p-3 flex-shrink-0">
            {item.imagen && !imgError ? (
                <img
                    src={item.imagen}
                    alt={item.titulo}
                    className="max-h-full max-w-full object-contain"
                    onError={() => setImgError(true)}
                />
            ) : (
                <Pill className="w-10 h-10 text-brand/50" />
            )}
        </div>
        <div className="flex-1 p-4">
            <h3 className="font-semibold text-foreground mb-2">{item.titulo}</h3>
            {item.cuando && (
                <p className="text-sm text-muted-foreground flex gap-2 mb-1">
                    <Clock className="w-4 h-4 text-brand flex-shrink-0 mt-0.5" />
                    <span><span className="font-semibold text-foreground">¿Cuándo? </span>{item.cuando}</span>
                </p>
            )}
            {item.cuanto && (
                <p className="text-sm text-muted-foreground flex gap-2 mb-1">
                    <Beaker className="w-4 h-4 text-brand flex-shrink-0 mt-0.5" />
                    <span><span className="font-semibold text-foreground">¿Cuánto? </span>{item.cuanto}</span>
                </p>
            )}
            {item.observaciones && (
                <p className="text-sm text-muted-foreground italic mt-2">{item.observaciones}</p>
            )}
            {item.enlaces?.length > 0 && (
                <div className="flex flex-wrap gap-3 mt-2">
                    {textosDeLosEnlaces(item.enlaces, item.titulo || item.nombre).map((texto, i) => (
                        <a key={i} href={urlDeEnlace(item.enlaces[i])} target="_blank" rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-xs text-brand hover:underline font-semibold">
                            <Link2 className="w-3 h-3" /> {texto}
                        </a>
                    ))}
                </div>
            )}
        </div>
    </div>
    );
};

/**
 * UNA LÍNEA DEL PROTOCOLO, COMO LA PIDE EL DOC DEL 16-08 (T2): el producto y, debajo,
 * «1 cacito · después de entrenar».
 *
 * La tarjeta de siempre partía la misma información en dos filas con sus rótulos
 * («¿Cuándo?» / «¿Cuánto?»), que es cómo se rellena en el panel, no cómo se lee: lo que
 * el cliente necesita saber de un vistazo es qué se toma y cuándo, en una frase.
 */
/**
 * EN QUÉ COMIDA SALE, CON SU NÚMERO (punto 184): «COMIDA 3», igual que en Inicio.
 *
 * `en_comidas` lo resuelve el servidor (`core/comida_del_suplemento`, punto 174) y viene en
 * SIMBÓLICO -- «primera», «última» -- porque quien sabe cuántas comidas hay es la pantalla que
 * tiene el día delante. Aquí no hay día, así que se usa el número de comidas que el cliente
 * tiene configurado (`GET /user/diet-config`), que es de donde sale también el reparto del
 * Inicio: si come cuatro veces, su cena es la Comida 4.
 *
 * Sin ese dato se dice «Primera comida» / «Última comida», que es verdad siempre: más vale
 * decirlo con palabras que dar un número que puede no ser el suyo.
 */
const dondeSale = (item, numComidas) => (item.en_comidas || [])
    .map((h) => {
        if (h === 'primera') return numComidas ? 'Comida 1' : 'Primera comida';
        if (h === 'ultima') return numComidas ? `Comida ${numComidas}` : 'Última comida';
        return `Comida ${String(h).replace('C', '')}`;
    })
    // Sin repetir: en un cliente de una sola comida, «desayuno y cena» son la misma.
    .filter((t, i, todas) => todas.indexOf(t) === i)
    .join(' y ');

/**
 * UN SUPLEMENTO SUYO, EN CUATRO LÍNEAS (punto 184 del 27-08):
 *
 *     Creatina
 *     COMIDA 3                       ← naranja, debajo del nombre
 *     Dosis — 5 g
 *     Cuándo — Todos los días, entrenes o no
 *     Comprar con descuento ↗
 *
 * LA COMIDA VA DESTACADA porque es lo que le hace falta a diario: «el cuándo y la dosis los
 * lee una vez y se los sabe; en qué comida va es lo que mira todos los días. Así abre la
 * pantalla y ve las tres comidas de un golpe, sin leer una línea». Y en el mismo naranja que
 * en Inicio, porque es el mismo dato.
 *
 * DOSIS Y CUÁNDO, SIN INTERROGACIÓN: «Dosis es lo que pone en los botes», y si una es etiqueta
 * la otra también. En ese orden, igual que la frase de arriba («dosis y cuándo tomarlo»).
 */
const LineaSuplemento = ({ item, numComidas }) => {
    const comida = dondeSale(item, numComidas);
    return (
        <div className="surface p-4 flex items-start gap-4" data-testid="supplement-card">
            {/* Del tamaño al que se ven en Calma, no el sello de 48 px de antes: el
                bote se reconoce de un vistazo, que es para lo que está la foto.
                Y SE PINCHA (Jesús, 6-09): lo pidió para la guía, pero aquí pasa lo mismo
                -- la pauta suya también enseña botes con su enlace de compra debajo --, así
                que el bote lleva a su página igual que allí. */}
            <ColumnaDeFotos fotos={fotosDe(item)} alt={item.titulo}
                enlacePorDefecto={urlDeEnlace(item.enlaces?.[0])} />
            <div className="min-w-0 flex-1">
                <p className="font-bold text-foreground text-sm">{item.titulo}</p>
                {/* `text-brand` (#FF671F) y NO `text-brand-orange` (#FFA500): el punto 184 pide
                    «el mismo dato y el mismo color que verá en Inicio», y en Inicio el
                    «+ Creatina» va en el naranja de la casa. El otro es el amarillo que él
                    mismo señaló en el punto 158 -- «ese amarillo no es de la casa». */}
                {comida && (
                    <p className="text-[11px] font-bold uppercase tracking-wider text-brand mt-0.5"
                        data-testid="suplemento-comida">
                        {comida}
                    </p>
                )}
                {item.cuanto && (
                    <p className="text-muted-foreground text-sm mt-1">
                        <span className="font-semibold text-foreground">Dosis </span>{item.cuanto}
                    </p>
                )}
                {item.cuando && (
                    <p className="text-muted-foreground text-sm">
                        <span className="font-semibold text-foreground">Cuándo </span>{item.cuando}
                    </p>
                )}
                {item.observaciones && <p className="text-muted-foreground text-xs italic mt-1">{item.observaciones}</p>}
                {item.enlaces?.length > 0 && (
                    <div className="flex flex-wrap gap-3 mt-1.5">
                        {textosDeLosEnlaces(item.enlaces, item.titulo || item.nombre).map((texto, i) => (
                            <a key={i} href={urlDeEnlace(item.enlaces[i])} target="_blank" rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 text-xs text-brand hover:underline font-semibold">
                                <Link2 className="w-3 h-3" /> {texto}
                            </a>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

/**
 * LA FICHA DE LA GUÍA (doc 19-08, bloque 08): Qué es · ¿Cuándo? · ¿Cuánto? y sus notas
 * sueltas, tal cual vienen de la web de Jesús.
 */
const FichaDeLaGuia = ({ ficha }) => (
    <div className="surface p-4 flex items-start gap-4" data-testid="ficha-guia">
        <ColumnaDeFotos fotos={fotosDe(ficha)} alt={ficha.nombre}
            enlacePorDefecto={urlDeEnlace(ficha.enlaces?.[0])} />
        <div className="min-w-0 flex-1">
            {/* EL ENCABEZADO, ENCIMA DEL NOMBRE (Jesús, 6-09): «Post-entreno
                recomendado:» delante del combo de Whey Isolate y crema de arroz. No es
                parte del nombre -- el nombre es lo que se toma, esto es cuándo se toma
                --, así que va como el rótulo de la comida en la pauta personal: pequeño,
                en mayúsculas y en el naranja de la casa, que es como esta pantalla marca
                ya el dato de un vistazo. La ficha que no lo traiga no pinta nada. */}
            {ficha.encabezado && (
                <p className="text-[11px] font-bold uppercase tracking-wider text-brand mb-0.5"
                    data-testid="encabezado-ficha">
                    {ficha.encabezado}
                </p>
            )}
            <p className="font-bold text-foreground text-sm">{ficha.nombre}</p>
            {ficha.que_es && <p className="text-muted-foreground text-sm mt-0.5">{ficha.que_es}</p>}
            {ficha.cuando && (
                <p className="text-sm text-muted-foreground mt-1">
                    <span className="font-semibold text-foreground">Cuándo </span>{ficha.cuando}
                </p>
            )}
            {ficha.cuanto && (
                <p className="text-sm text-muted-foreground">
                    <span className="font-semibold text-foreground">Dosis </span>{ficha.cuanto}
                </p>
            )}
            {ficha.notas && <p className="text-muted-foreground text-xs italic mt-1">{ficha.notas}</p>}
            {ficha.enlaces?.length > 0 && (
                <div className="flex flex-wrap gap-3 mt-1.5">
                    {textosDeLosEnlaces(ficha.enlaces, ficha.titulo || ficha.nombre).map((texto, i) => (
                        <a key={i} href={urlDeEnlace(ficha.enlaces[i])} target="_blank" rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-xs text-brand hover:underline font-semibold">
                            <Link2 className="w-3 h-3" /> {texto}
                        </a>
                    ))}
                </div>
            )}
            {ficha.enlaces_alternativos?.length > 0 && (
                <EnlacesAlternativos enlaces={ficha.enlaces_alternativos} />
            )}
        </div>
    </div>
);

/**
 * EL DESCUENTO, CON EL TEXTO QUE DICTÓ JESÚS EL 21-08 (apartado 10 de su doc), tal cual.
 * Antes era una línea recompuesta con los campos («GALLEGOVIP · 20 % de descuento en
 * FullGas...») y solo salía dentro de la guía: ahora el texto es el suyo, el código se
 * copia con un toque, y sale también en la pantalla del protocolo, que no lo tenía.
 */
const Descuento = () => {
    const [copiado, setCopiado] = useState(false);
    const copiar = async () => {
        try {
            await navigator.clipboard.writeText('GALLEGOVIP');
            setCopiado(true);
            setTimeout(() => setCopiado(false), 2000);
        } catch (e) {
            // Sin permiso de portapapeles el código sigue a la vista: no pasa nada.
            console.error('No se pudo copiar el código de descuento', e);
        }
    };
    return (
        <div className="surface p-4 border-dashed" data-testid="descuento-guia">
            <p className="text-sm text-foreground">
                Tienes un código especial en FullGas, la empresa con la que llevo trabajando más de 15 años.
            </p>
            <div className="my-2 flex items-center gap-2">
                <button onClick={copiar} type="button" title="Copiar el código" data-testid="copiar-descuento"
                    className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-brand/10 text-brand font-bold tracking-widest text-sm hover:bg-brand/20">
                    GALLEGOVIP
                    {copiado ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </button>
                {copiado && <span className="text-xs font-semibold text-brand">Copiado</span>}
            </div>
            {/* «Activo TODO EL TIEMPO, mientras dure tu suscripción» decía las dos cosas: que
                es para siempre y que se acaba. Y «te aplicarán» empuja al de al lado -- es
                FullGas quien lo aplica, no nosotros --, cuando lo que hay que decir es qué
                tiene. Una sola frase, sin la contradicción y sin el tercero. */}
            <p className="text-sm text-foreground">
                Lo tendrás activo mientras dure tu suscripción, con un 20 % en toda la web.
            </p>
            {/* Y LA PUERTA DE LA TIENDA (Francisco, 11-09, punto 11). Se le daba el código y
                se le dejaba buscando la web: un descuento sin tienda no lo usa nadie. En
                pestaña nueva, que si no se le saca de la app en mitad de su protocolo. */}
            <a href="https://fullgas.org" target="_blank" rel="noopener noreferrer"
                data-testid="ir-a-fullgas"
                className="inline-flex items-center gap-1.5 mt-2 text-sm font-bold text-brand hover:underline">
                Ir a FullGas <ExternalLink className="w-3.5 h-3.5" />
            </a>
        </div>
    );
};

/**
 * EL TEXTO DE ENTRADA DE LA GUÍA, EL SUYO (24-08).
 *
 * Jesús lo escribió en su web, se importó a la base (`app_state.guia_suplementacion`) y el
 * endpoint lo sirve desde entonces, pero esta pantalla pintaba una versión recortada
 * escrita aquí a mano y no lo leía: editarlo en la base no cambiaba nada de lo que ve
 * nadie. Ahora manda el suyo -- que además trae el párrafo de la guía de definición del
 * Catálogo Premium, que aquí se había quedado fuera -- y el de casa queda de respaldo,
 * por si esta base no ha pasado por la importación.
 *
 * Del suyo se cae UNA línea: el «*IMPORTANTE» del descuento, porque va justo debajo en su
 * propio bloque con el texto que dictó el 21-08. Decírselo dos veces en la misma pantalla
 * no es ser literal, es repetirse.
 */
// Y EL DE RESPALDO, PALABRA POR PALABRA IGUAL QUE EL DEL SERVIDOR (regla de las voces,
// 30-08). Este decía «se recomienda empezar por los básicos»: el impersonal, que no es ni
// la casa ni Jesús y suena a prospecto. La guía es criterio suyo y va en primera persona
// -- «los suplementos que YO más recomiendo», «TE recomiendo empezar por los básicos» --,
// que es como ya está en `core/guia_suplementacion.TEXTO_DE_LA_GUIA`.
//
// Se copia en vez de importarse porque esto es justo el respaldo de cuando el servidor no
// contesta; pero son el mismo texto, y si se cambia uno se cambia el otro.
const TEXTO_DE_ENTRADA_DE_CASA = 'Estos son los suplementos que yo más recomiendo con pautas '
    + 'exactas de uso. Están organizados por categorías según su función. '
    + 'En condiciones normales, te recomiendo empezar por los básicos.';

// LO QUE NO SE LE PINTA AL CLIENTE, aunque venga en el texto de la base.
//
// El «*IMPORTANTE» del descuento va justo debajo en su propio bloque: decirlo dos veces en
// la misma pantalla no es ser literal, es repetirse.
//
// Y EL PÁRRAFO DEL CATÁLOGO PREMIUM SE CAE (punto 182 del 27-08): «el que vende el Catálogo
// Premium dentro de lo que ya ha pagado». Aquí hay dos decisiones encontradas y manda la
// última: el 24-08 se recuperó el texto entero de Jesús -- y con él este párrafo, que la
// versión de casa no traía -- y tres días después él pidió que se cayera. Se filtra al
// pintar y no se le toca el texto en la base, que es suyo y lo usa en más sitios.
const FUERA_DEL_TEXTO = [
    (p) => p.startsWith('*IMPORTANTE'),
    (p) => /cat[áa]logo premium/i.test(p),
];

const TextoDeEntrada = ({ texto }) => {
    const suyos = (texto || '').split('\n').map(p => p.trim())
        .filter(p => p && !FUERA_DEL_TEXTO.some(fuera => fuera(p)));
    const parrafos = suyos.length > 0 ? suyos : [TEXTO_DE_ENTRADA_DE_CASA];
    return (
        <div className="mb-5 max-w-2xl space-y-2" data-testid="texto-entrada-guia">
            {parrafos.map((p, i) => <p key={i} className="text-muted-foreground text-sm">{p}</p>)}
        </div>
    );
};

/**
 * LA GUÍA (doc 19-08, presentada como dictó Jesús el 21-08): primero LAS CATEGORÍAS,
 * para pinchar y entrar, no todo el mensaje de golpe. Dentro de cada una, sus fichas.
 * El remate por plan sigue igual (el aviso del plan personalizado o la oferta de los 87).
 * La guía habla en singular porque es de Jesús; el aviso y la oferta en plural, porque
 * los hace el equipo.
 */
const GuiaDeSuplementacion = ({ api, guia, conDescuento = true }) => {
    // La categoría abierta (su clave), o null: la portada con las categorías.
    const [abierta, setAbierta] = useState(null);
    const [subfiltro, setSubfiltro] = useState(null);
    const [pidiendo, setPidiendo] = useState(false);

    if (!guia) return null;
    // SIETE CATEGORÍAS Y NINGÚN CAJÓN DE SASTRE (punto 187 del 27-08). Aquí había una entrada
    // más, «El resto de la guía», con las fichas que no encajaban en ninguna. Sus palabras:
    // «es una categoría que no dice qué tiene dentro, y no debería existir. Si hay alguno que
    // no encaja en ninguna, el problema es que falta una categoría, no que haga falta un cajón
    // de sastre».
    //
    // Antes de quitarlo se contó: eran DOS fichas de 28, y se recolocaron -- la citrulina a
    // Rendimiento y la crema RELIEF EFFECT a Descanso (`_colocar_huerfanos_guia_2708.py`).
    // Si algún día vuelve a entrar una sin categoría, NO se le inventa un sitio: se queda
    // fuera de la guía y hay que ponerle la suya, que es lo que dice el punto.
    const entradas = (guia.secciones || []).filter(s => s.suplementos.length > 0);
    const seccion = entradas.find(s => s.clave === abierta) || null;

    const pedirRevision = async () => {
        setPidiendo(true);
        try {
            const { data } = await api.post('/billing/ajuste-a-medida/checkout', {});
            if (data?.checkout_url) window.location.href = data.checkout_url;
        } catch (e) {
            console.error('[oferta 87] no se pudo abrir el checkout', e);
        } finally {
            setPidiendo(false);
        }
    };

    return (
        <section className="max-w-2xl" data-testid="guia-suplementacion">
            {/* EL AVISO DEL QUE ESTÁ ESPERANDO (punto 183 del 27-08). Decía «Esto es solo la
                guía básica. En unos días recibirás tu plan de suplementación personalizado»,
                que es de los dos párrafos que el punto 182 tira: promete un plazo que no
                controlamos y llama «básica» a la guía entera de Jesús.
                Ahora dice qué pasa y qué va a pasar, sin fecha. */}
            {guia.aviso_plan_personalizado && (
                <div className="surface bg-brand/[0.06] border-brand/30 p-4 mb-5" data-testid="aviso-guia-basica">
                    <p className="text-sm text-foreground font-medium">
                        Te lo estamos preparando. Te avisamos en cuanto esté.
                    </p>
                    <p className="text-sm text-muted-foreground mt-1">
                        Mientras tanto, aquí tienes todo lo que uso y recomiendo, con pautas
                        exactas de uso.
                    </p>
                </div>
            )}

            {!seccion ? (
                /* LA PORTADA (Jesús, 21-08): debajo del texto corto, directamente las
                   categorías para pinchar y entrar. El mensaje entero de golpe murió. */
                <div className="space-y-3">
                    {entradas.map(s => (
                        <button key={s.clave} type="button" data-testid={`categoria-${s.clave}`}
                            onClick={() => { setAbierta(s.clave); setSubfiltro(null); }}
                            className="surface w-full p-4 flex items-center justify-between gap-3 text-left hover:border-brand/40 transition-colors">
                            <span className="min-w-0">
                                <span className="block font-bold text-foreground text-sm">{s.nombre}</span>
                                {s.explicacion && (
                                    <span className="block text-muted-foreground text-xs mt-0.5">{s.explicacion}</span>
                                )}
                            </span>
                            <ChevronRight className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                        </button>
                    ))}
                </div>
            ) : (
                /* DENTRO DE UNA CATEGORÍA: su explicación literal, sus fichas, y la vuelta. */
                <div>
                    <button type="button" data-testid="volver-categorias"
                        onClick={() => { setAbierta(null); setSubfiltro(null); }}
                        className="inline-flex items-center gap-1 text-sm font-semibold text-brand mb-3 hover:underline">
                        <ChevronLeft className="w-4 h-4" /> Todas las categorías
                    </button>
                    <h2 className="font-heading text-xl font-bold uppercase text-foreground mb-1">{seccion.nombre}</h2>
                    {seccion.explicacion && <p className="text-muted-foreground text-sm mb-3">{seccion.explicacion}</p>}
                    {/* Los seis apartados de Salud, solo dentro de Salud. */}
                    {seccion.clave === 'salud' && seccion.subfiltros?.length > 0 && (
                        <div className="flex flex-wrap gap-2 mb-4">
                            {seccion.subfiltros.map(sf => (
                                <button key={sf} onClick={() => setSubfiltro(subfiltro === sf ? null : sf)}
                                    className={`px-2.5 py-1 rounded-full text-[11px] font-semibold ${subfiltro === sf ? 'bg-brand/20 text-brand' : 'bg-muted text-muted-foreground'}`}>
                                    {sf}
                                </button>
                            ))}
                        </div>
                    )}
                    <div className="space-y-3">
                        {(subfiltro && seccion.clave === 'salud'
                            ? seccion.suplementos.filter(f => (f.subfiltros || []).some(x => x.toLowerCase() === subfiltro.toLowerCase()))
                            : seccion.suplementos
                        ).map(f => <FichaDeLaGuia key={f.id} ficha={f} />)}
                    </div>
                </div>
            )}

            {/* El descuento, con el texto de Jesús. En la pantalla del protocolo lo pinta
                la propia pantalla junto a la pauta, y aquí se apaga para no repetirlo. */}
            {conDescuento && <div className="mt-6"><Descuento /></div>}

            {/* LA OFERTA, AL FINAL Y SOLO A QUIEN NO LO TIENE (doc 19-08): «entra a
                consultar qué tomar y lo último que ve es lo que le falta». El botón va al
                mismo checkout que la oferta del final del alta. */}
            {guia.oferta_87 && (
                <div className="surface p-5 mt-6 border-brand/30" data-testid="oferta-87-guia">
                    <p className="font-bold text-foreground mb-1">¿Quieres tu plan de suplementación personalizado?</p>
                    <p className="text-sm text-muted-foreground mb-3">
                        Va incluido con la revisión de tus macros: te los ajustamos a medida
                        y te preparamos tu suplementación con ellos.
                    </p>
                    <button onClick={pedirRevision} disabled={pidiendo} data-testid="solicitar-revision-87"
                        className="w-full sm:w-auto px-5 py-2.5 rounded-full bg-brand text-white text-sm font-bold hover:opacity-90 disabled:opacity-50">
                        {pidiendo ? 'Abriendo el pago...' : 'Solicitar la revisión de mis macros · 87 €'}
                    </button>
                </div>
            )}
        </section>
    );
};

const SupplementsPage = () => {
    const { api, pantalla } = useAuth();
    // El interruptor del panel (doc 16-08): encendido -- que es como nace -- la pantalla
    // habla como el documento; apagado se queda la de siempre, sin desplegar nada.
    const nuevo = pantalla('t2_suplementos', true);
    const [protocol, setProtocol] = useState(null);
    const [guia, setGuia] = useState(null);
    // Cuantas comidas tiene puesto, para decir «Comida 4» y no «ultima comida» (punto 184).
    const [numComidas, setNumComidas] = useState(null);
    const [loading, setLoading] = useState(true);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { fetchProtocol(); }, []);

    const fetchProtocol = async () => {
        try {
            const res = await api.get('/supplements/current');
            setProtocol(res.data);
        } catch (e) {
            console.error('Error fetching supplements:', e);
        }
        // La guía la ven todos, tenga o no protocolo; si falla, la pantalla sigue con lo
        // que haya.
        // El numero de comidas del cliente: si falla, la comida se dice con palabras.
        try {
            const c = await api.get('/user/diet-config');
            setNumComidas(c.data?.num_comidas || null);
        } catch { /* se queda en palabras */ }
        try {
            const g = await api.get('/supplements/guia');
            setGuia(g.data);
        } catch (e) {
            console.error('No se pudo traer la guía de suplementación:', e);
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return <Wrap><div className="animate-pulse space-y-4">
            <div className="h-9 bg-muted rounded w-1/3" />
            <div className="h-24 bg-muted rounded-2xl" />
            <div className="h-24 bg-muted rounded-2xl" />
        </div></Wrap>;
    }

    const tieneActual = protocol?.actual?.length > 0;
    const tieneSiguiente = protocol?.siguiente?.length > 0;
    // La general compuesta murió con el doc 19-08: sin protocolo propio, lo que se ve es
    // LA GUÍA entera de Jesús.
    const esGenerica = !!protocol?.es_generica;
    const hayGuia = (guia?.secciones || []).some(s => s.suplementos.length > 0) || (guia?.sin_seccion || []).length > 0;

    /**
     * LOS TRES ESTADOS DE ESTA PANTALLA (puntos 179 y 180 del 27-08).
     *
     *   Con plan y con protocolo   →  «Mis suplementos». Sólo lo suyo. LA GUÍA NO SALE.
     *   Con plan, sin protocolo    →  «Mis suplementos», el aviso de que se lo estás
     *                                 preparando, y la guía debajo para que tenga algo.
     *   Sin plan personalizado     →  «Suplementación»: la guía, y nada más.
     *
     * Hasta hoy eran dos y ninguno de los tres: quien ya tenía su pauta veía ADEMÁS la guía
     * entera debajo, y los dos casos se llamaban «Tu suplementación», que es lo que denuncia
     * el 180 -- «en uno de los dos es mentira». El «mis» es lo que dice que son suyos.
     *
     * Y de qué estado se trata lo dice el SERVIDOR (`con_plan`), no el código de error de la
     * petición del protocolo: antes se distinguía porque `/supplements/current` devuelve 403 a
     * quien no tiene el plan, y eso funcionaba de casualidad.
     */
    const conPlan = !!guia?.con_plan;

    if (!tieneActual && !tieneSiguiente && !protocol?.nota && hayGuia) {
        return <Wrap>
            <h1 className="font-heading text-3xl md:text-4xl font-bold uppercase text-foreground mb-2">
                {conPlan ? 'Mis suplementos' : 'Suplementación'}
            </h1>
            {/* Con plan, el aviso manda y el texto de la guía no se repite: lo dice el propio
                aviso («mientras tanto, aquí tienes todo lo que uso y recomiendo»), y sin el
                «empieza por los básicos», que es lo que pide el punto 183. */}
            {!conPlan && <TextoDeEntrada texto={guia?.texto_entrada} />}
            <GuiaDeSuplementacion api={api} guia={guia} />
        </Wrap>;
    }

    if (!protocol || (!tieneActual && !tieneSiguiente && !protocol.nota)) {
        return <Wrap>
            {/* AQUÍ SE HABÍA QUEDADO «TU SUPLEMENTACIÓN» (30-08). El punto 180 la quitó de
                las otras dos pantallas -- «Mis suplementos» cuando es suyo, «Suplementación»
                cuando es la guía --, pero esta tercera, la de cuando no hay ni protocolo ni
                guía que enseñar, seguía con el nombre viejo. Y encima decidía con `nuevo`,
                que es el interruptor de la pantalla, no si el cliente tiene plan: por eso no
                se vio. Misma señal que arriba, `conPlan`. */}
            <h1 className="font-heading text-3xl md:text-4xl font-bold uppercase text-foreground mb-6">
                {conPlan ? 'Mis suplementos' : 'Suplementación'}
            </h1>
            <div className="surface p-10 text-center">
                <div className="w-16 h-16 bg-brand/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <Pill className="w-8 h-8 text-brand/60" />
                </div>
                {/* YA NO HAY NADA QUE TRAER (punto 4.15, cerrado el 09-08-2026). Este texto
                    decía «lo tienes en el sistema anterior y lo estamos trayendo», que era
                    verdad mientras los protocolos seguían en Calma. Ya no: se migraron los 98
                    clientes que tenían uno, con su histórico entero. Quien llegue aquí ahora es
                    porque de verdad no tiene protocolo puesto, y hay que decírselo tal cual en
                    vez de hacerle esperar algo que no va a llegar.

                    El texto anterior hablaba además de 666 clientes pendientes: un dato interno
                    nuestro que no le decía nada a quien lo leía. */}
                {/* NI «PROTOCOLO» NI «TU ENTRENADOR» (#53 del 15-08: «sigue el lenguaje de
                    catálogo»). «Protocolo» es como lo llamamos nosotros por dentro; el
                    cliente compró que le digamos qué tomar. Y «tu entrenador» le habla de
                    una persona que casi nadie tiene asignada en la app: aquí quien contesta
                    es el equipo. */}
                {/* AQUÍ SOLO SE LLEGA SI NO HAY NI GENERAL (18-08). Este cartel era lo que
                    veía todo el que no tuviera la suya escrita, y es justo lo que Jesús no
                    quiere: mientras no le pongamos la suya, se le enseña la general. El
                    servidor la manda siempre que el catálogo tenga base o intra, así que
                    esto ya solo sale si el catálogo está vacío. */}
                {/* Y FUERA EL CHAT TAMBIÉN AQUÍ (punto 180). El título ya se arregló esta
                    mañana; lo que seguía era este texto, que le manda a una puerta que puede
                    no ser suya -- el chat solo lo llevan el Gold y el Premium -- y le pide
                    que sea él quien reclame. Si se lo estamos preparando nosotros, no hay
                    nada que reclamar: se le dice qué espera y que estamos en ello.

                    «Tu plan de suplementación personalizado» y no «suplementación» a secas:
                    a quien tiene plan se le está preparando el suyo, y esa es la diferencia
                    que el 179 pide que se note. */}
                <h2 className="font-heading text-xl font-bold uppercase text-foreground mb-2">
                    {conPlan ? 'Todavía no tienes tu plan de suplementación personalizado'
                        : 'Todavía no tienes suplementación'}
                </h2>
                <p className="text-muted-foreground text-sm">
                    Estamos en ello, te avisamos en cuanto esté.
                </p>
            </div>
        </Wrap>;
    }

    const Tarjeta = nuevo ? LineaSuplemento : SupplementCard;

    return (
        <Wrap>
            {/* «MIS SUPLEMENTOS», CON EL MIS (punto 180): son suyos. La guía se llama
                «Suplementación», sin el mis, porque es la guía y no la suya. Hoy se llamaban
                las dos igual y en una de las dos era mentira. */}
            {/* Y LA SEÑAL ES EL PLAN, NO EL INTERRUPTOR (Gonzalo, 4-09, puntos 81/82 y 85;
                cerrado el 13-09). Esta tercera pantalla -- la del que SÍ tiene su pauta -- era
                la única que seguía decidiendo el título con `nuevo`, que es el interruptor de
                `pantalla('t2_suplementos')` del panel y no dice nada del cliente. Las otras
                dos ya se arreglaron el 30-08 y quedó escrito ahí arriba («Misma señal que
                arriba, `conPlan`»); esta se quedó atrás. Con el interruptor apagado, quien
                tiene su protocolo personalizado leía «SUPLEMENTACIÓN» encima de sus propios
                suplementos, que es justo la mentira que denuncia el 180. */}
            <h1 className="font-heading text-3xl md:text-4xl font-bold uppercase text-foreground mb-2">
                {conPlan ? 'Mis suplementos' : 'Suplementación'}
            </h1>
            {/* EL SUBTÍTULO ES SUYO, NO UN DESCARGO. Lo que había avisaba de que esto es
                «orientativo» y que «pueden ser necesarios otros suplementos», que es lo que
                se le dice a quien mira un catálogo general. Esto no es un catálogo: es lo
                que le ha pautado a él. Y dice en una línea qué hay dentro, con las mismas
                palabras que las filas de cada suplemento (punto 181). */}
            <p className="text-muted-foreground text-sm mb-5 max-w-2xl">
                {esGenerica
                    ? 'Todavía no te he pautado la tuya. Mientras tanto, esta es la que recomiendo de base: en cuanto te ponga la tuya, la ves aquí.'
                    : nuevo
                        ? 'Lo que tienes pautado, dosis y cuándo tomarlo.'
                        : 'Aquí ves algunos de los suplementos más habituales que recomiendo, así como su modo de empleo. Esta información es orientativa: pueden ser necesarios otros suplementos o dosis según tu situación, objetivos o tolerancias.'}
            </p>

            {protocol.nota && (
                <div className="surface bg-brand/[0.04] border-brand/20 p-4 mb-5 flex gap-3 max-w-2xl">
                    <StickyNote className="w-5 h-5 text-brand flex-shrink-0 mt-0.5" />
                    <div>
                        <p className="text-[11px] font-bold text-brand uppercase tracking-wider mb-1">Nota personal</p>
                        <p className="text-sm text-muted-foreground leading-relaxed">{protocol.nota}</p>
                    </div>
                </div>
            )}

            {tieneActual && (
                <section className="mb-7">
                    {/* Sin rótulo cuando no hay nada más: la pantalla entera ya dice qué es
                        esto, y «Suplementación actual» solo hace falta para distinguirla de
                        la que entra más adelante. */}
                    {(!nuevo || tieneSiguiente) && (
                        <h2 className="caption mb-3">
                            {esGenerica ? 'La suplementación general' : 'Suplementación actual'}
                        </h2>
                    )}
                    <div className={nuevo ? 'space-y-3 max-w-2xl' : 'grid md:grid-cols-2 gap-3'}>
                        {protocol.actual.map((it, i) => <Tarjeta key={i} item={it} numComidas={numComidas} />)}
                    </div>
                </section>
            )}

            {tieneSiguiente && (
                <section>
                    <div className="flex items-center gap-2 mb-3">
                        <h2 className="caption">Suplementación siguiente</h2>
                        {protocol.siguiente_fecha && (
                            <span className="inline-flex items-center gap-1 text-[11px] font-bold uppercase px-2 py-0.5 rounded-full bg-red-500/10 text-red-500">
                                <CalendarClock className="w-3 h-3" /> A partir del {formatDate(protocol.siguiente_fecha)}
                            </span>
                        )}
                    </div>
                    <div className={nuevo ? 'space-y-3 max-w-2xl' : 'grid md:grid-cols-2 gap-3'}>
                        {protocol.siguiente.map((it, i) => <Tarjeta key={i} item={it} numComidas={numComidas} />)}
                    </div>
                </section>
            )}

            {/* EL DESCUENTO, EN LA PANTALLA DEL PROTOCOLO (Jesús, 21-08): antes solo
                vivía dentro de la guía y quien miraba su pauta no lo veía. */}
            <div className="max-w-2xl mt-8"><Descuento /></div>

            {/* AQUÍ IBA LA GUÍA ENTERA, Y SE VA (punto 179 del 27-08): «con plan y con
                protocolo puesto → Mis suplementos: SÓLO LO SUYO. La guía no sale».
                El doc del 19-08 decía «la ven todos», y por eso estaba; pero el que ya tiene
                su pauta no viene aquí a leer un catálogo, viene a mirar qué le toca hoy, y
                debajo de tres líneas suyas había veintiocho fichas que no son suyas.
                Quien no tiene pauta sí la sigue viendo: es el segundo estado, arriba. */}
        </Wrap>
    );
};

export default SupplementsPage;
